import React, { useState, useEffect, useRef } from 'react';
import './SearchInterface.css';

const SearchInterface = ({
  search,
  onRun,
  loading,
  hideFilteredItems,
  onToggleHideFiltered,
}) => {
  const [keyword, setKeyword] = useState('');
  const [sellerId, setSellerId] = useState('');
  const [pages, setPages] = useState(1);
  const [sortBy, setSortBy] = useState('12'); // 12 = Best Match, 10 = Newly Listed
  const [countryId, setCountryId] = useState('US');
  const [schedule, setSchedule] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [runSecondarySearches, setRunSecondarySearches] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scheduledNotification, setScheduledNotification] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update form when selectedSearch changes
  useEffect(() => {
    if (search) {
      setKeyword(search.keyword || '');
      setSellerId(search.seller_id || '');
      setPages(search.number_of_pages || 1);
      setSortBy(search.sort_by || '12');
      setCountryId(search.country_id || 'US');
      setSchedule(search.schedule || '');
      setIsScheduled(search.schedule !== null);
    } else {
      // Reset form for new search
      setKeyword('');
      setSellerId('');
      setPages(1);
      setSortBy('12');
      setCountryId('US');
      setSchedule('');
      setIsScheduled(false);
    }
  }, [search]);

  const buildSearchData = (includeSchedule) => ({
    keyword: keyword.trim() || null,
    seller_id: sellerId.trim() || null,
    number_of_pages: parseInt(pages),
    sort_by: parseInt(sortBy),
    country_id: countryId,
    schedule: includeSchedule ? parseInt(schedule) : null,
    run_secondary_searches: runSecondarySearches,
  });

  const validateForm = (includeSchedule) => {
    if (!keyword.trim() && !sellerId.trim()) {
      alert('Please enter either a keyword or seller ID');
      return false;
    }
    if (includeSchedule && (!schedule || schedule < 1 || schedule > 365)) {
      alert('Schedule must be between 1 and 365 days');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isScheduled) {
      if (!validateForm(false)) return;
      onRun(buildSearchData(false));
    }
    // When isScheduled, the form submit is a no-op; use dropdown actions
  };

  const handleSearchAndSchedule = () => {
    setDropdownOpen(false);
    if (!validateForm(true)) return;
    onRun(buildSearchData(true));
    setScheduledNotification(true);
    setTimeout(() => setScheduledNotification(false), 4000);
  };

  const handleOnlySchedule = () => {
    setDropdownOpen(false);
    if (!validateForm(true)) return;
    onRun({ ...buildSearchData(true), onlySchedule: true });
    setScheduledNotification(true);
    setTimeout(() => setScheduledNotification(false), 4000);
  };

  const isRunning = loading || search?.state === 'in_progress';

  return (
    <div className="search-interface">
      {scheduledNotification && (
        <div className="scheduled-notification">
          Search Scheduled
        </div>
      )}
      <form onSubmit={handleSubmit} className="search-form">
        <div className="form-row">
          <div className="form-col-6">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Keywords"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                disabled={isRunning}
              />
              {isScheduled ? (
                <div className="dropdown-btn-group" ref={dropdownRef}>
                  <button
                    type="button"
                    className="btn btn-primary dropdown-main-btn"
                    disabled={isRunning}
                    onClick={handleSearchAndSchedule}
                  >
                    {isRunning ? 'Running...' : 'Search and Schedule'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary dropdown-toggle-btn"
                    disabled={isRunning}
                    onClick={() => setDropdownOpen((o) => !o)}
                    aria-expanded={dropdownOpen}
                  >
                    ▾
                  </button>
                  {dropdownOpen && (
                    <ul className="dropdown-menu-custom">
                      <li>
                        <button type="button" onClick={handleSearchAndSchedule}>
                          Search and Schedule
                        </button>
                      </li>
                      <li>
                        <button type="button" onClick={handleOnlySchedule}>
                          Only Schedule
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              ) : (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isRunning}
                >
                  {isRunning ? 'Running...' : 'Search'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-col-6">
            <input
              type="text"
              className="form-control"
              placeholder="Seller ID"
              value={sellerId}
              onChange={(e) => setSellerId(e.target.value)}
              disabled={isRunning}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-row form-row-half">
            <div className="form-col-third">
              <div className="input-group">
                <span className="input-group-text">Pages</span>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  max="10"
                  value={pages}
                  onChange={(e) => setPages(e.target.value)}
                  disabled={isRunning}
                />
              </div>
            </div>

            <div className="form-col-third">
              <select
                className="form-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                disabled={isRunning}
              >
                <option value="12">Best Match</option>
                <option value="10">Newly Listed</option>
              </select>
            </div>

            <div className="form-col-third">
              <select
                className="form-select"
                value={countryId}
                onChange={(e) => setCountryId(e.target.value)}
                disabled={isRunning}
              >
                <option value="US">United States</option>
                <option value="DE">Germany</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-col-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="scheduledCheckbox"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.target.checked)}
                disabled={isRunning}
              />
              <label className="form-check-label" htmlFor="scheduledCheckbox">
                Create scheduled search
              </label>
            </div>
          </div>

          <div className="form-col-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="runSecondarySearchesCheckbox"
                checked={runSecondarySearches}
                onChange={(e) => setRunSecondarySearches(e.target.checked)}
                disabled={isRunning}
              />
              <label className="form-check-label" htmlFor="runSecondarySearchesCheckbox">
                Run secondary searches
              </label>
            </div>
          </div>

          <div className="form-col-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="hideFilteredCheckbox"
                checked={hideFilteredItems}
                onChange={onToggleHideFiltered}
              />
              <label className="form-check-label" htmlFor="hideFilteredCheckbox">
                Hide Filtered Out Items
              </label>
            </div>
          </div>
        </div>

        {isScheduled && (
          <div className="form-row">
            <div className="form-col-6">
              <div className="input-group">
                <span className="input-group-text">Every</span>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Days"
                  min="1"
                  max="365"
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  disabled={isRunning}
                />
                <span className="input-group-text">days</span>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchInterface;
